import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import rootReducer from '@/store/rootReducer';

type RootState = ReturnType<typeof rootReducer>;

const initialState: Space.FormData = {
  buildingId: null,
  spaceCategoryPresetId: null,
  floors: [],
};

const spaceFormStore = createSlice({
  name: 'spaceForm',
  initialState: initialState,
  reducers: {
    /**
     * 공간 폼 상태의 값을 변경한다.
     */
    setSpaceFormData: (
      state,
      { payload }: PayloadAction<Partial<Space.FormData>>
    ) => {
      state = {
        ...state,
        ...payload,
      };
      return state;
    },

    /**
     * 공간 폼 상태의 값을 초기화
     */
    resetSpaceFormData: (state) => {
      state = initialState;
      return state;
    },

    /**
     * 층 정보를 업데이트
     */
    setBuildingFloors: (
      state,
      { payload }: PayloadAction<Space.FormData['floors']>
    ) => {
      state.floors = payload;
      return state;
    },

    /**
     * 층 정보를 초기화
     */
    resetBuildingFloors: (state) => {
      state['floors'] = [];
      return state;
    },

    /**
     * 층 내부의 공간 유형과 그 하위의 공간 리스트를 초기화
     * 공간유형을 변경하였을때 작동함
     */
    resetSpaces: (state) => {
      state['floors'] = state['floors'].map((floor) => {
        floor['spaceCategories'] = floor['spaceCategories']?.map(
          (spaceCategory) => {
            if (!spaceCategory) return spaceCategory;
            spaceCategory['spaces'] = [];
            return spaceCategory;
          }
        );
        return floor;
      });
      return state;
    },

    /**
     * 특정 층에서 공간유형을 추가할때, 배열로 이루어진 상태값을 업데이트합니다.
     * "공간 유형 추가" 버튼을 클릭할때 트리거됩니다.
     */
    createSpaceCategoryArrayData: (
      state,
      {
        payload: { floorIdx },
      }: PayloadAction<{
        floorIdx: number;
      }>
    ) => {
      state.floors[floorIdx].spaceCategories.push({
        id: null,
        spaces: [],
      });
    },

    /**
     * 특정 층에서의 공간유형을 업데이트 합니다.
     */
    setSpaceCategory: (
      state,
      {
        payload: { floorIdx, spaceIdx, spaceCategoryId },
      }: PayloadAction<{
        floorIdx: number;
        spaceIdx: number;
        spaceCategoryId: number;
      }>
    ) => {
      // @ts-ignore
      state.floors[floorIdx].spaceCategories[spaceIdx]['id'] = spaceCategoryId;
    },

    /**
     * 특정 층의 공간유형과 그 유형에 속한 공간에 대한 데이터를 삭제합니다.
     */
    removeSpaces: (
      state,
      {
        payload: { floorIdx, spaceCategoryIdx },
      }: PayloadAction<{
        floorIdx: number;
        spaceCategoryIdx: number;
      }>
    ) => {
      state.floors[floorIdx].spaceCategories?.splice(spaceCategoryIdx, 1);
    },

    /**
     * 특정 층의, 특정 공간유형내에 속한 공간들의 리스트를 업데이트합니다.
     * @todo 개발 편의상 Array 전체를 Hard-Update 하는 방식임 추후에는 변경된 부분만 업데이트 하도록 변경필요
     */
    setSpaceArray: (
      state,
      {
        payload: { floorIdx, spaceCategoryIdx, spaces },
      }: PayloadAction<{
        floorIdx: number;
        spaceCategoryIdx: number;
        spaces: Space.Item[];
      }>
    ) => {
      // @ts-ignore
      state.floors[floorIdx].spaceCategories[spaceCategoryIdx].spaces = spaces;
      return state;
    },

    /**
     * 특정 층에 대한 값을 업데이트 합니다.
     */
    setFloorData: (
      state,
      {
        payload: { floorIdx, value },
      }: PayloadAction<{
        floorIdx: number;
        value: Partial<Space.FormData['floors'][0]>;
      }>
    ) => {
      state.floors[floorIdx] = {
        ...state.floors[floorIdx],
        ...value,
      };
      return state;
    },
  },
});

export const spaceFormDataSelector = (state: RootState) => state.space;
export const spaceFormStoreActions = spaceFormStore.actions;
export default spaceFormStore.reducer;
